import React, { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TooltipProvider } from "@/components/ui/tooltip";
import AddPaymentModal from "./AddPaymentModal";
import ExpenseDeleteDialog from "./ExpenseDeleteDialog";
import { useCampaignPayments, useUpdatePaymentStatus } from "@/hooks/useCampaignPayments";
import { useCampaignExpenses, useAddCampaignExpense, useDeleteCampaignExpense } from "@/hooks/useCampaignExpenses";
import { useCampaignBudgetCalculations } from "@/hooks/useCampaignBudgetCalculations";
import { useOrganization } from "@/contexts/OrganizationContext";
import { InfluencerFeesTab } from "./InfluencerFeesTab";
import { CampaignExpensesTab } from "./CampaignExpensesTab";
import { useAuth } from "@/contexts/AuthContext";
import { formatCurrency } from "@/utils/currency";
import { CreditCard, Receipt } from "lucide-react";
interface Props {
  influencers: any[];
  expenses: any[];
  campaignId: string;
  campaignInfluencers: any[];
}

const CampaignFinancialTabs: React.FC<Props> = ({ influencers, campaignId, campaignInfluencers }) => {
  const { currentOrganization } = useOrganization();
  const { userCountry } = useAuth();
  const [newExpense, setNewExpense] = useState({
    date: '',
    amount: '',
    category: '',
    description: '',
    vendor: ''
  });
  const [addPaymentModalOpen, setAddPaymentModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("payments");

  // Fetch real payments data from Supabase
  const { data: paymentsData = [], isLoading: loadingPayments } = useCampaignPayments(campaignId);
  const updatePaymentStatusMutation = useUpdatePaymentStatus();

  // Fetch real expenses data from Supabase
  const { data: expensesData = [], isLoading: loadingExpenses } = useCampaignExpenses(campaignId);
  const addExpenseMutation = useAddCampaignExpense();
  const deleteExpenseMutation = useDeleteCampaignExpense();

  // Fetch budget calculations
  const { data: budgetData, isLoading: loadingBudget } = useCampaignBudgetCalculations(campaignId);

  const handleAddExpense = async () => {
    if (!newExpense.date || !newExpense.amount || !newExpense.category) {
      return;
    }

    const expenseData = {
      campaign_id: campaignId,
      organization_id: currentOrganization?.id || '',
      date: newExpense.date,
      amount: parseFloat(newExpense.amount),
      category: newExpense.category,
      description: newExpense.description || undefined,
      vendor: newExpense.vendor || undefined,
    };

    addExpenseMutation.mutate(expenseData, {
      onSuccess: () => {
        // Reset form
        setNewExpense({
          date: '',
          amount: '',
          category: '',
          description: '',
          vendor: ''
        });
      }
    });
  };

  const handleDeleteExpenseClick = (expenseId: string) => {
    setExpenseToDelete(expenseId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (expenseToDelete) {
      deleteExpenseMutation.mutate({ expenseId: expenseToDelete, campaignId }, {
        onSuccess: () => {
          setDeleteDialogOpen(false);
          setExpenseToDelete(null);
        }
      });
    }
  };

  const handleMarkAsPaid = (paymentId: string) => {
    updatePaymentStatusMutation.mutate({ paymentId, status: 'paid' });
  };

  // Transform payments data for display
  const transformedPayments = paymentsData.map(payment => {
    const influencer = payment.campaign_influencers?.influencers;
    const campaignInfluencer = payment.campaign_influencers;

    return {
      id: payment.id,
      influencerName: influencer?.name || 'Unknown',
      handle: influencer?.handle || 'N/A',
      platform: influencer?.platform || 'N/A',
      deliverables: campaignInfluencer?.deliverables || 'TBD',
      contentStatus: campaignInfluencer?.status || 'contacted',
      baseFee: Number(payment.amount || 0), // amount now stores base fee
      gstAmount: Number(payment.gst_amount || 0),
      tdsAmount: Number(payment.tds_amount || 0),
      netPayable: Number(payment.net_payable || 0), // Use DB calculated value
      status: payment.status || 'pending',
      dueDate: payment.due_date,
      paymentMode: payment.payment_mode || 'UPI',
      notes: payment.notes || ''
    };
  });

  return (
    <TooltipProvider>
      <div className="p-6 rounded-2xl border border-slate-200">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Financial Management</h2>

          {/* Budget Summary Cards */}
          {budgetData && (
            <div className="flex gap-4 text-sm">
              <div className="bg-blue-50 px-3 py-2 rounded-lg">
                <div className="text-blue-600 font-medium">Total Budget</div>
                <div className="text-blue-800 font-semibold">{formatCurrency(budgetData.totalBudget, userCountry)}</div>
              </div>
              <div className="bg-red-50 px-3 py-2 rounded-lg">
                <div className="text-red-600 font-medium">Amount Spent</div>
                <div className="text-red-800 font-semibold">{formatCurrency(budgetData.amountSpent, userCountry)}</div>
              </div>
              <div className="bg-green-50 px-3 py-2 rounded-lg">
                <div className="text-green-600 font-medium">Remaining Budget</div>
                <div className="text-green-800 font-semibold">{formatCurrency(budgetData.remainingBudget, userCountry)}</div>
              </div>
            </div>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex h-14 w-full items-stretch rounded-xl border border-slate-200 bg-white p-0 shadow-sm overflow-hidden mb-6">
            <TabsTrigger
              value="payments"
              className="relative flex flex-1 items-center justify-center bg-transparent transition-all duration-200 text-slate-800 data-[state=active]:!text-[#A655F8] data-[state=active]:!bg-[#A655F8]/[0.06] data-[state=active]:!shadow-none focus-visible:outline-none py-0 px-2 rounded-none font-['Hanken_Grotesk',sans-serif]"
            >
              <div className="relative flex items-center gap-1.5 md:gap-2 h-full">
                <CreditCard className="h-4 w-4 md:h-4.5 md:w-4.5" />
                <span className="text-xs sm:text-sm md:text-[15px] font-semibold whitespace-nowrap">Influencers & Payments</span>
                {activeTab === 'payments' && (
                  <div className="absolute bottom-0 left-0 right-0 h-[2.5px] rounded-t-full bg-[#A655F8]" />
                )}
              </div>
            </TabsTrigger>

            <div className="h-6 w-[1px] bg-slate-200 self-center shrink-0" />

            <TabsTrigger
              value="expenses"
              className="relative flex flex-1 items-center justify-center bg-transparent transition-all duration-200 text-slate-800 data-[state=active]:!text-[#A655F8] data-[state=active]:!bg-[#A655F8]/[0.06] data-[state=active]:!shadow-none focus-visible:outline-none py-0 px-2 rounded-none font-['Hanken_Grotesk',sans-serif]"
            >
              <div className="relative flex items-center gap-1.5 md:gap-2 h-full">
                <Receipt className="h-4 w-4 md:h-4.5 md:w-4.5" />
                <span className="text-xs sm:text-sm md:text-[15px] font-semibold whitespace-nowrap">Expenses</span>
                {activeTab === 'expenses' && (
                  <div className="absolute bottom-0 left-0 right-0 h-[2.5px] rounded-t-full bg-[#A655F8]" />
                )}
              </div>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="payments" className="mt-6">
            <InfluencerFeesTab
              campaignInfluencers={campaignInfluencers}
              setAddPaymentModalOpen={setAddPaymentModalOpen}
              budgetData={budgetData}
              loadingPayments={loadingPayments}
              transformedPayments={transformedPayments}
              handleMarkAsPaid={handleMarkAsPaid}
              updatePaymentStatusMutation={updatePaymentStatusMutation}
            />
          </TabsContent>

          <TabsContent value="expenses" className="mt-6">
            <CampaignExpensesTab
              newExpense={newExpense}
              setNewExpense={setNewExpense}
              handleAddExpense={handleAddExpense}
              addExpenseMutation={addExpenseMutation}
              loadingExpenses={loadingExpenses}
              expensesData={expensesData}
              handleDeleteExpenseClick={handleDeleteExpenseClick}
              deleteExpenseMutation={deleteExpenseMutation}
            />
          </TabsContent>
        </Tabs>

        <AddPaymentModal
          open={addPaymentModalOpen}
          onOpenChange={setAddPaymentModalOpen}
          campaignInfluencers={campaignInfluencers}
        />

        <ExpenseDeleteDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={handleConfirmDelete}
          isDeleting={deleteExpenseMutation.isPending}
        />
      </div>
    </TooltipProvider>
  );
};

export default CampaignFinancialTabs;
